import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { X } from 'lucide-react';
import API_BASE_URL, { GOOGLE_CLIENT_ID } from '../../config';
import './InviteDashboard.css';

export const InviteLogin: React.FC = () => {
    const { inviteLogin, inviteGoogleLogin, error } = useInviteAuth();
    const history = useHistory();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        const sendRemoteLog = (message: string, level: string = 'info') => {
            fetch(`${API_BASE_URL}/api/invite/auth/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message, level })
            }).catch(() => {});
        };

        const initializeAndRenderGoogle = () => {
            const google = (window as any).google;
            if (google) {
                console.log('[InviteLogin] Initializing Google with Client ID:', GOOGLE_CLIENT_ID);
                sendRemoteLog(`[InviteLogin] Initializing Google with Client ID: ${GOOGLE_CLIENT_ID}`);
                google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleResponse,
                });
                const signInBtn = document.getElementById('google-signin-btn');
                if (signInBtn) {
                    google.accounts.id.renderButton(
                        signInBtn,
                        { theme: 'outline', size: 'large', width: '100%', text: 'signin_with' }
                    );
                }
                const signUpBtn = document.getElementById('google-signup-btn');
                if (signUpBtn) {
                    google.accounts.id.renderButton(
                        signUpBtn,
                        { theme: 'filled_blue', size: 'large', width: '100%', text: 'signup_with' }
                    );
                }
            } else {
                console.warn('[InviteLogin] Google object not found on window');
                sendRemoteLog(`[InviteLogin] Google object not found on window`, 'warn');
            }
        };

        sendRemoteLog(`[InviteLogin] Mounting component. Client ID configured: ${GOOGLE_CLIENT_ID}`);

        let script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement;
        
        const handleScriptLoad = () => {
            sendRemoteLog(`[InviteLogin] Google library script loaded successfully`);
            initializeAndRenderGoogle();
        };

        if (script) {
            const google = (window as any).google;
            if (google) {
                initializeAndRenderGoogle();
            } else {
                script.addEventListener('load', handleScriptLoad);
            }
        } else {
            script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
            script.addEventListener('load', handleScriptLoad);
        }

        return () => {
            if (script) {
                script.removeEventListener('load', handleScriptLoad);
            }
        };
    }, []);

    const handleGoogleResponse = async (response: any) => {
        setLoading(true);
        setFormError(null);
        try {
            await inviteGoogleLogin(response.credential);
            history.push('/invite/dashboard');
        } catch (err: any) {
            setFormError(err.message || 'Google Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        if (!email || !password) {
            setFormError('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            await inviteLogin(email, password);
            history.push('/invite/dashboard');
        } catch (err: any) {
            setFormError(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="invite-auth-container">
            <div className="invite-auth-card">
                <Link to="/" className="invite-auth-close" aria-label="Close page">
                    <X size={18} />
                </Link>
                <div className="auth-header">
                    <h2>✉️ Welcome Back</h2>
                    <p>Log in to manage your invitation campaigns</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {(formError || error) && (
                        <div className="auth-error-badge">
                            ⚠️ {formError || error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            placeholder="e.g. nitin@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button type="submit" className="auth-submit-btn" disabled={loading}>
                        {loading ? 'Logging In...' : 'Log In 🚀'}
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0', color: 'var(--invite-text-light)' }}>
                        <hr style={{ flex: 1, border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' }} />
                        <span style={{ padding: '0 10px', fontSize: '0.85rem' }}>or</span>
                        <hr style={{ flex: 1, border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' }} />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
                        <div id="google-signin-btn" style={{ width: '100%' }}></div>
                        <div id="google-signup-btn" style={{ width: '100%' }}></div>
                    </div>
                </form>

                <div className="auth-footer">
                    <p>Don't have an account? <Link to="/invite/register">Sign Up</Link></p>
                </div>
            </div>
        </div>
    );
};
export default InviteLogin;
